import { useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import {
  Sofa, Snowflake, Luggage, ShieldCheck, Sparkles, Plane, PlaneTakeoff, MapPin,
  Building2, Clock, FileText, Tag, Home, ArrowRight, Phone, MessageSquare, Check,
} from "lucide-react";
import { useSEO } from "@/lib/seo";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/1bb89d72-67bd-439a-b04d-415fc0b99be3/images/3b4f394b8531fa8c0f56b3161f016252431fc26a2c817d0c53bb3fd22795adec.jpeg";
const PHONE = "+19703156500";
const CANONICAL = "https://www.aspenexecutivelimos.com/fleet";

const GALLERY = [
  { src: "https://static.prod-images.emergentagent.com/jobs/1bb89d72-67bd-439a-b04d-415fc0b99be3/images/2c722d50e2f1f0829ac2cbf9d571618976f83f4997dc45f623c38b59b026f426.jpeg", title: "Spacious Comfort", caption: "Room to stretch out and relax on every journey." },
  { src: "https://static.prod-images.emergentagent.com/jobs/1bb89d72-67bd-439a-b04d-415fc0b99be3/images/3f678f6d2e9e73a4a685b4b0e18dd3807b2558f6eff2bd631437704e879fa9c4.jpeg", title: "Refined Detail", caption: "Premium materials and meticulous craftsmanship." },
  { src: "https://static.prod-images.emergentagent.com/jobs/1bb89d72-67bd-439a-b04d-415fc0b99be3/images/31ab7eab4a57958ef9689a3d39df7217de2581809265d57ef705d580b79c650d.jpeg", title: "Generous Cargo", caption: "Ample space for luggage, skis, and gear." },
];

const CTA_BTN = "inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-bold text-base tracking-wide rounded-sm px-[3px] py-[1px] shadow-xl hover:bg-[#E0BC4A] transition-colors";

const FEATURES = [
  { icon: Sofa, title: "Luxury Interior", desc: "Comfortable seating, climate control, and a quiet cabin for a premium passenger experience." },
  { icon: Snowflake, title: "Mountain Capability", desc: "Four-wheel drive and confident handling built for Colorado winter performance." },
  { icon: Luggage, title: "Luggage Capacity", desc: "Ideal for airport transportation, ski equipment, golf clubs, and vacation luggage." },
  { icon: ShieldCheck, title: "Safety & Reliability", desc: "Meticulously maintained vehicles operated by professional local chauffeurs." },
];

const STANDARDS = ["Luxury", "Cleanliness", "Safety", "Professional appearance", "Passenger comfort", "Reliability"];

const USES = [
  { icon: Plane, title: "Airport Transportation", desc: "Chauffeured transfers to and from Aspen, Denver, Eagle, Rifle, and Grand Junction.", to: "/airport-transportation", testid: "use-airport" },
  { icon: PlaneTakeoff, title: "Private Jet Transportation", desc: "Planeside FBO pickups and drop-offs for a seamless private aviation experience.", to: "/airport-transportation", testid: "use-jet" },
  { icon: MapPin, title: "Local Transportation", desc: "Dining, shopping, events, and destinations throughout Aspen and the Valley.", to: "/aspen-area-transportation", testid: "use-local" },
  { icon: Building2, title: "Corporate Transportation", desc: "Punctual, discreet service for executives and visiting clients.", to: "/#services", testid: "use-corporate" },
  { icon: Clock, title: "Hourly Chauffeur Service", desc: "A car and chauffeur at your disposal by the hour, the day, or the week.", to: "/#services", testid: "use-hourly" },
];

const RELATED = [
  { icon: Plane, title: "Airport Transportation", to: "/airport-transportation", testid: "related-airport" },
  { icon: MapPin, title: "Aspen Area Transportation", to: "/aspen-area-transportation", testid: "related-aspen" },
  { icon: Tag, title: "Rates", to: "/rates", testid: "related-rates" },
  { icon: Building2, title: "Corporate Transportation", to: "/#services", testid: "related-corporate" },
  { icon: Clock, title: "Hourly Hire", to: "/#services", testid: "related-hourly" },
  { icon: FileText, title: "Request a Quote", to: "/reserve", testid: "related-quote" },
  { icon: Home, title: "Home", to: "/", testid: "related-home" },
];

const FAQS = [
  { q: "What type of vehicles do you use?", a: "We specialize in luxury four-wheel-drive SUVs, selected for passenger comfort, spacious interiors, safety, and a polished, professional presentation." },
  { q: "Are your vehicles equipped for Colorado winter driving?", a: "Yes. Our four-wheel-drive SUVs and experienced local chauffeurs are built for Colorado's mountain roads and changing winter weather conditions, so you travel safely and confidently." },
  { q: "How much luggage will your SUVs hold?", a: "Our SUVs offer generous cargo space — ideal for airport luggage, ski and snowboard equipment, golf clubs, and everything you need for a mountain getaway." },
  { q: "Do you provide transportation for ski vacations?", a: "Absolutely. Our SUVs are perfect for ski trips, with room for gear and all-weather capability to get you to the slopes and back with ease." },
  { q: "Can I request transportation for a group?", a: "Yes. Our spacious SUVs comfortably accommodate groups. Share your party size and itinerary and we'll tailor the right arrangement for you." },
];

const SEO_DESC = "Aspen Executive Limo's luxury four-wheel-drive SUVs — built for comfort, safety, and Colorado mountain travel. Premium private car service for airport, local, corporate, and hourly transportation in Aspen.";

const BUSINESS_LD = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://www.aspenexecutivelimos.com/#business",
  name: "Aspen Executive Limo",
  description: SEO_DESC,
  url: CANONICAL,
  image: HERO_IMG,
  telephone: "+1-970-315-6500",
  priceRange: "$$$",
  address: { "@type": "PostalAddress", addressLocality: "Aspen", addressRegion: "CO", addressCountry: "US" },
  areaServed: [
    { "@type": "City", name: "Aspen, CO" },
    { "@type": "City", name: "Snowmass, CO" },
    { "@type": "City", name: "Vail, CO" },
    { "@type": "City", name: "Denver, CO" },
  ],
  makesOffer: {
    "@type": "Offer",
    itemOffered: {
      "@type": "Service",
      name: "Luxury SUV Transportation",
      serviceType: "Private chauffeured luxury four-wheel-drive SUV transportation",
      areaServed: "Aspen and the Colorado Rockies",
    },
  },
};

const BREADCRUMB_LD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://www.aspenexecutivelimos.com/" },
    { "@type": "ListItem", position: 2, name: "Fleet", item: CANONICAL },
  ],
};

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};

export default function Fleet() {
  useSEO({
    title: "Luxury SUVs Built for Colorado | Aspen Executive Limo Fleet",
    description: SEO_DESC,
    keywords: "Aspen luxury SUV, Aspen private transportation, Aspen luxury car service, Aspen airport SUV, Colorado mountain transportation, luxury SUV transportation Aspen",
    canonical: CANONICAL,
    image: HERO_IMG,
    jsonLd: [BUSINESS_LD, BREADCRUMB_LD, FAQ_LD],
  });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen" data-testid="fleet-page">
      <Navbar />

      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 z-0" aria-hidden="true">
          <img src={HERO_IMG} alt="Luxury black Chevrolet Suburban in an Aspen mountain setting" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-[120px] pb-24 sm:pt-[160px] sm:pb-32">
          <p className="text-xs tracking-label uppercase font-bold text-[#D4AF37] mb-4" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.9)" }}>Aspen Executive Limo</p>
          <h1 className="font-heading text-5xl sm:text-7xl font-light leading-[1.05] tracking-tight text-white max-w-3xl" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.9)" }}>
            Luxury SUVs Built for Colorado
          </h1>
          <p className="mt-6 max-w-2xl text-lg sm:text-xl text-zinc-100 font-light leading-relaxed" style={{ textShadow: "0 1px 6px rgba(0,0,0,0.85)" }}>
            Travel in comfort, confidence, and style with luxury four-wheel-drive SUVs designed for Aspen's mountain roads and changing weather conditions.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/reserve" data-testid="fleet-reserve-btn" className={CTA_BTN}>Reserve Your Ride <ArrowRight size={16} /></Link>
            <a href={`sms:${PHONE}`} data-testid="fleet-calltext-btn" className={CTA_BTN}><MessageSquare size={16} /> Call or Text Now</a>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white">The Standard of the Rockies</h2>
        <div className="mt-4 h-px w-16 bg-[#D4AF37]/60 mx-auto" />
        <p className="mt-6 text-lg text-zinc-300 font-light leading-relaxed">
          Aspen Executive Limo specializes in luxury four-wheel-drive SUVs chosen for passenger comfort, safety, and reliability. Every vehicle offers spacious interiors, dependable winter performance, and a polished, professional presentation — so whether you're arriving for a ski week or heading to a mountain-top dinner, you travel in true comfort and confidence.
        </p>
      </section>

      {/* VEHICLE FEATURES */}
      <section className="bg-black/40 backdrop-blur-md border-y border-white/10 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white text-center">Designed for the Journey</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} data-testid={`fleet-feature-${f.title}`} className="group bg-[#101012] border border-white/10 rounded-sm p-8 hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all duration-500">
                <f.icon size={26} strokeWidth={1.5} className="gold-text mb-5" />
                <h3 className="font-heading text-xl mb-2 text-white">{f.title}</h3>
                <p className="text-zinc-400 font-light text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LUXURY EXPERIENCE GALLERY */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="fleet-gallery">
        <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white text-center">The Luxury Experience</h2>
        <div className="mt-4 h-px w-16 bg-[#D4AF37]/60 mx-auto" />
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {GALLERY.map((g) => (
            <figure key={g.title} data-testid={`gallery-${g.title}`} className="group relative overflow-hidden rounded-sm border border-white/10 hover:border-[#D4AF37]/40 transition-colors">
              <div className="aspect-[4/5] overflow-hidden">
                <img src={g.src} alt={g.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
              <figcaption className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="font-heading text-xl text-white">{g.title}</h3>
                <p className="mt-1 text-zinc-300 font-light text-sm leading-relaxed">{g.caption}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* WHY OUR VEHICLES */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white">Why Our Vehicles</h2>
        <div className="mt-4 h-px w-16 bg-[#D4AF37]/60 mx-auto" />
        <p className="mt-6 text-lg text-zinc-300 font-light leading-relaxed">
          Every vehicle we dispatch meets the Aspen Executive Limo standard — held to the same expectations on every reservation.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {STANDARDS.map((s) => (
            <span key={s} data-testid={`fleet-standard-${s}`} className="inline-flex items-center gap-2 bg-[#101012] border border-white/10 rounded-sm px-4 py-2 text-sm text-zinc-200">
              <Check size={14} className="gold-text" /> {s}
            </span>
          ))}
        </div>
      </section>

      {/* SERVICE DURING HIGH DEMAND */}
      <section className="bg-black/40 backdrop-blur-md border-y border-white/10 py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Sparkles size={26} strokeWidth={1.5} className="gold-text mx-auto mb-5" />
          <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white">Reliable Transportation Every Time</h2>
          <p className="mt-6 text-lg text-zinc-300 font-light leading-relaxed">
            During periods of exceptionally high demand, Aspen Executive Limo works with carefully selected transportation partners who meet our standards for professionalism, safety, vehicle quality, and customer service. Every reservation receives the same commitment to excellence and personalized attention.
          </p>
        </div>
      </section>

      {/* BEST USES */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white text-center">Where Our SUVs Take You</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {USES.map((u) => (
            <Link key={u.title} to={u.to} data-testid={u.testid} className="group bg-black/40 backdrop-blur-md border border-white/10 rounded-sm p-8 hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all duration-500">
              <u.icon size={26} strokeWidth={1.5} className="gold-text mb-5" />
              <h3 className="font-heading text-xl mb-2 text-white flex items-center gap-2">
                {u.title}
                <ArrowRight size={18} className="gold-text opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </h3>
              <p className="text-zinc-400 font-light text-sm leading-relaxed">{u.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-20" data-testid="fleet-faq">
        <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white text-center">Frequently Asked Questions</h2>
        <div className="mt-4 h-px w-16 bg-[#D4AF37]/60 mx-auto" />
        <div className="mt-10 space-y-4">
          {FAQS.map((f, i) => (
            <div key={i} data-testid={`fleet-faq-item-${i}`} className="bg-[#101012] border border-white/10 rounded-sm p-6 hover:border-[#D4AF37]/40 transition-colors">
              <h3 className="font-heading text-xl text-white">{f.q}</h3>
              <p className="mt-3 text-zinc-400 font-light text-sm leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* RELATED / INTERNAL LINKS */}
      <section className="bg-black/40 backdrop-blur-md border-y border-white/10 py-20" data-testid="fleet-related">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white text-center">Continue Planning Your Trip</h2>
          <div className="mt-4 h-px w-16 bg-[#D4AF37]/60 mx-auto" />
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {RELATED.map((r) => (
              <Link key={r.title} to={r.to} data-testid={r.testid} className="group inline-flex items-center gap-2 bg-[#101012] border border-white/10 rounded-sm px-5 py-3 text-sm text-zinc-200 hover:border-[#D4AF37]/40 hover:text-white transition-all">
                <r.icon size={16} className="gold-text" /> {r.title}
                <ArrowRight size={14} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all gold-text" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white">Travel in Comfort. Arrive with Confidence.</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/reserve" data-testid="fleet-cta-reserve" className={CTA_BTN}>Reserve Your Ride <ArrowRight size={16} /></Link>
            <a href={`tel:${PHONE}`} data-testid="fleet-cta-call" className={CTA_BTN}><Phone size={16} /> Call or Text Now</a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
